/**
 * Schemas Zod e tipos da API de comida, cardápio, estoque e água (7.5, 7.6, 7.7).
 * A API valida com os schemas; o front usa os tipos.
 */
import { z } from 'zod';
import type { HHMM, ISODate } from '../time';
import type { MealSlot, OrderStatus, StockCategory, StockLocation } from '../types';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const mealSlot = z.enum(['breakfast', 'lunch', 'snack', 'dinner', 'supper']);
/** aceita o slot em inglês ou o nome da rota (cafe, almoco, lanche, jantar, ceia) */
const ROUTE_TO_SLOT: Record<string, MealSlot> = { cafe: 'breakfast', almoco: 'lunch', lanche: 'snack', jantar: 'dinner', ceia: 'supper' };
export const mealSlotParam = z
  .string()
  .transform((s) => ROUTE_TO_SLOT[s] ?? s)
  .pipe(mealSlot);

// ── 7.5 comida ──────────────────────────────────────────────────────────────

export const mealsQuery = z.object({ date: isoDate.optional() });
export const mealOptionsQuery = z.object({ slot: mealSlotParam, date: isoDate.optional() });

export const mealCreateBody = z.object({
  clientId: z.string().uuid().optional(),
  date: isoDate.optional(),
  slot: mealSlotParam,
  optionId: z.string().uuid().nullish(),
  foodId: z.string().uuid().nullish(),
  portion: z.union([z.literal(1), z.literal(0.75), z.literal(0.5)]).default(1),
  /** códigos (OVOS2, FRANGO, PAO_SAB) ou ids de `meal_extras` */
  extras: z.array(z.string()).default([]),
  isFreeMeal: z.boolean().default(false),
  note: z.string().max(500).nullish(),
});
export type MealCreateBody = z.input<typeof mealCreateBody>;

export const mealPatchBody = z.object({
  optionId: z.string().uuid().nullish(),
  foodId: z.string().uuid().nullish(),
  portion: z.union([z.literal(1), z.literal(0.75), z.literal(0.5)]).optional(),
  extras: z.array(z.string()).optional(),
  isFreeMeal: z.boolean().optional(),
  note: z.string().max(500).nullish(),
});
export type MealPatchBody = z.input<typeof mealPatchBody>;

export const menuSwapBody = z.object({ slot: z.enum(['lunch', 'dinner']), foodId: z.string().uuid() });
export type MenuSwapBody = z.infer<typeof menuSwapBody>;

export type StockComponentView = { stock: string; qty: number };

/** Uma escolha da lista (café A/B/C, lanche, ceias, marmitas, refeição livre). */
export type MealChoice = {
  kind: 'option' | 'marmita' | 'free';
  /** optionId ou foodId ('free' na refeição livre) */
  id: string;
  code: string;
  name: string;
  shortName: string;
  /** linha de detalhe do cartão escuro: prep_note, ou "Liv Up · 325 g · 68 g carbo · 9,4 g gordura" */
  detail: string;
  kcal: number | null;
  proteinG: number | null;
  /** marmitas: saldo disponível para esta refeição ("N no estoque") */
  qty: number | null;
  /** "3 ovos, 2 fatias de pão de forma, requeijão, 1 chá" ("Sai do estoque") */
  stockText: string;
  components: StockComponentView[];
  isPlanned: boolean;
  blocked: boolean;
  /** "só sábado à noite" etc. */
  blockedText: string | null;
};

export type MealExtraChoice = {
  id: string;
  code: string;
  name: string;
  /** título curto: "2 ovos cozidos", "Frango desfiado cremoso", "+1 pão francês" */
  title: string;
  /** "155 kcal · 13 g · do plano" */
  info: string;
  kcal: number;
  proteinG: number;
  rule: string;
  defaultOn: boolean;
  stockText: string;
  /** saldo do item de estoque do complemento */
  qty: number | null;
};

export type MealLogView = {
  id: string;
  clientId: string | null;
  date: ISODate;
  slot: MealSlot;
  optionId: string | null;
  foodId: string | null;
  portion: number;
  isFreeMeal: boolean;
  kcal: number | null;
  proteinG: number | null;
  note: string | null;
  loggedAt: string;
  /** HH:MM local */
  time: HHMM;
  extras: string[];
  /** nome do que foi registrado */
  name: string;
  description: string;
};

export type MealSlotView = {
  slot: MealSlot;
  label: string;
  short: string;
  route: string;
  time: HHMM;
  /** kcal do dia antes desta refeição (registradas ou planejadas) */
  kcalBefore: number;
  planned: { kind: 'option' | 'marmita'; id: string; code: string; name: string; description: string; kcal: number; proteinG: number; extras: string[] } | null;
  log: MealLogView | null;
  /** título da lista de opções: "ESCOLHA O CAFÉ", "TROCAR POR OUTRA DO ESTOQUE"… */
  optionsTitle: string;
  choices: MealChoice[];
  extras: MealExtraChoice[];
};

export type FoodScreenAlert = { code: string; tone: 'warn' | 'info' | 'good'; kanji: string; title: string; body: string };

export type MealsDayResponse = {
  date: ISODate;
  now: HHMM | null;
  kcalTarget: number;
  kcalFloor: number;
  proteinTarget: number;
  isGameDay: boolean;
  cycleDay: number | null;
  cycleLength: number | null;
  totals: { kcalLogged: number; proteinLogged: number; kcalProjected: number; proteinProjected: number; hasFreeMeal: boolean };
  freeMeal: { allowed: boolean; reason: string | null; text: string };
  slots: MealSlotView[];
  /** avisos de tela (screen:comida): dia de jogo, refeição livre liberada */
  alerts: FoodScreenAlert[];
};

export type StockMovementView = {
  id: string;
  itemId: string;
  code: string;
  name: string;
  qty: number;
  location: StockLocation | null;
  reason: string;
  text: string;
};

export type MealSaveResponse = {
  data: MealLogView;
  stockMovements: StockMovementView[];
  warnings: string[];
  /** avisos para a tela de Comida (meal.no_protein) */
  screenAlerts: FoodScreenAlert[];
  today: unknown;
  alerts: unknown[];
};

export type MenuDayView = {
  id: string;
  dayIndex: number;
  date: ISODate;
  lunch: { foodId: string; code: string; shortName: string; kcal: number; proteinG: number };
  dinner: { foodId: string; code: string; shortName: string; kcal: number; proteinG: number };
  kcal: number;
  proteinG: number;
};

export type MenuCurrentResponse = {
  cycle: { id: string; startDate: ISODate; lengthDays: number; orderId: string | null } | null;
  today: { date: ISODate; dayIndex: number | null };
  days: MenuDayView[];
};

// ── 7.6 estoque ─────────────────────────────────────────────────────────────

const location = z.enum(['freezer', 'fridge', 'pantry']);
export const stockMovementBody = z.object({
  itemId: z.string().uuid(),
  qty: z.number().refine((n) => n !== 0, 'Quantidade não pode ser 0'),
  reason: z.enum(['purchase', 'adjust', 'waste', 'move']),
  from: location.nullish(),
  to: location.nullish(),
  note: z.string().max(200).nullish(),
});
export type StockMovementBody = z.input<typeof stockMovementBody>;

export const eggsCookBody = z.object({ count: z.number().int().min(1).max(30).default(10) });
export const shoppingListQuery = z.object({
  days: z.coerce.number().int().min(1).max(60).default(15),
  mode: z.enum(['missing', 'full']).default('full'),
});
export const checkoutBody = z.object({
  items: z.array(z.object({ itemId: z.string().uuid(), qty: z.number().positive() })).min(1),
});
export type CheckoutBody = z.infer<typeof checkoutBody>;
/** contagem: quanto tem agora (na unidade do item); 0 = acabou */
export const stockCountBody = z.object({ qty: z.number().min(0).max(1_000_000) });

export const ordersQuery = z.object({ vendor: z.enum(['livup', 'market']).optional() });
export const orderRepeatBody = z.object({ status: z.enum(['draft', 'ordered']).default('draft') }).default({});
export const orderPatchBody = z.object({
  status: z.enum(['draft', 'ordered', 'cancelled']),
  expectedAt: isoDate.nullish(),
});

export type MarmitaStockView = {
  itemId: string;
  code: string;
  name: string;
  qty: number;
  fridge: number;
  freezer: number;
  /** quantidade do último pedido (barra "1 de 4") */
  total: number;
  tag: 'hoje' | 'amanhã' | null;
};

export type MarketStockView = {
  itemId: string;
  code: string;
  name: string;
  short: string;
  category: StockCategory;
  unit: string;
  qty: number;
  /** "2 caixas", "1,55 L", "20 un." */
  qtyText: string;
  packageLabel: string | null;
  packageQty: number | null;
  /** passo do seletor de quantidade (uma embalagem; sem embalagem, 100 g/ml ou 1) */
  step: number;
  /** "450 ml/dia", "sábado e café B" */
  useText: string;
  dailyUse: number | null;
  daysLeft: number | null;
  lastDay: ISODate | null;
  status: 'ok' | 'low' | 'tomorrow' | 'out';
  /** "acaba amanhã", "até sáb", "12 dias", "ok" */
  label: string;
  tone: 'shu' | 'warn' | 'neutral';
  occasional: boolean;
  balances: { fridge: number; freezer: number; pantry: number };
};

export type OrderItemView = { id: string; foodId: string | null; stockItemId: string | null; name: string; qty: number; unit: string; unitPriceBrl: number | null };
export type OrderView = {
  id: string;
  vendor: 'livup' | 'market';
  status: OrderStatus;
  totalBrl: number | null;
  units: number;
  createdAt: string;
  orderedAt: string | null;
  expectedAt: ISODate | null;
  receivedAt: string | null;
  items: OrderItemView[];
  freeShipping: boolean;
};

export type StockAlertView = { code: string; tone: 'warn' | 'info'; kanji: string; title: string; body: string; actionLabel: string | null };

export type StockResponse = {
  date: ISODate;
  summary: {
    marmitas: { count: number; of: number; lastDay: ISODate | null; daysLeft: number; text: string; tone: 'warn' | 'shu' | 'muted' };
    market: { count: number; text: string; tone: 'shu' | 'warn' | 'muted' };
  };
  order: {
    /** needed: cartão ocre; draft: rascunho aberto; ordered: cartão verde 済; none: nada a mostrar */
    state: 'none' | 'needed' | 'draft' | 'ordered';
    text: string;
    draft: OrderView | null;
    ordered: OrderView | null;
    repeat: { units: number; totalBrl: number; freeShipping: boolean };
  };
  places: { fridge: { count: number; label: string }; freezer: { count: number; label: string } };
  /** tarefa das 21:00 (freezer → geladeira); null quando as de amanhã já estão na geladeira */
  task: { time: HHMM; text: string; moves: { code: string; qty: number }[] } | null;
  marmitas: MarmitaStockView[];
  /** mercado e suplementos do plano, com saldo e dias */
  market: MarketStockView[];
  /** itens de uso eventual, fora do plano padrão */
  optional: MarketStockView[];
  eggs: { cooked: number; raw: number; needCook: boolean };
  alerts: StockAlertView[];
};

export type ShoppingListLine = {
  itemId: string;
  code: string;
  name: string;
  missing: number;
  packages: number | null;
  /** sugerido, na unidade do item */
  qty: number;
  unit: string;
  text: string;
  packageLabel: string | null;
  packageQty: number | null;
  step: number;
  /** saldo agora, na unidade do item */
  have: number;
  /** saldo − o que ainda sai hoje */
  leftover: number;
  dailyUse: number | null;
};

export type ShoppingListResponse = {
  days: number;
  mode: 'missing' | 'full';
  items: ShoppingListLine[];
  optional: { itemId: string; code: string; name: string; unit: string }[];
  text: string;
};

// ── 7.7 água ────────────────────────────────────────────────────────────────

export const waterQuery = z.object({ date: isoDate.optional() });
export const waterCreateBody = z.object({
  clientId: z.string().uuid().optional(),
  ml: z.number().int().min(1).max(2000),
  date: isoDate.optional(),
});
export type WaterCreateBody = z.input<typeof waterCreateBody>;

export type WaterLogView = { id: string; clientId: string | null; ml: number; time: HHMM; loggedAt: string; note: string };
export type WaterDayResponse = {
  date: ISODate;
  isGameDay: boolean;
  /** "Dia de treino" | "Dia de jogo" */
  dayLabel: string;
  target: number;
  total: number;
  expectedNow: number;
  behind: number;
  milestones: { label: string; ml: number; due: HHMM; cumulative: number; done: boolean; current: boolean }[];
  logs: WaterLogView[];
  /** "Treino às 18:30: leve 750 ml pra academia. Próximo lembrete às 17:00." */
  hint: string;
};
